import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import KycForm from "../KycForm";
import { getData, GET_USER_DETAILS_URL } from "../../utils/api";
import { getLoginDetails, getToken, storeLoginDetails } from "../../utils/storage";
import "./kyc-pending-overlay.css";

const SESSION_CLOSED_KEY = "kyc-form-closed";

const PUBLIC_PREFIXES = [
  "/",
  "/invite",
  "/signup",
  "/franchise-signup",
  "/logout",
];

function isPublicPath(pathname) {
  if (PUBLIC_PREFIXES.includes(pathname)) return true;
  if (pathname.startsWith("/login/")) return true;
  return false;
}

function normalizeUserDetailsPayload(data) {
  if (!data || typeof data !== "object") return {};
  return data.data !== undefined ? data.data : data;
}

function readIsKYCSubmitted(root) {
  if (!root || typeof root !== "object") return false;
  const v = root.isKYCSubmitted;
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "true" || s === "1" || s === "yes") return true;
  }
  return false;
}

const PROFILE_POLL_MS = 5000;

export default function KycPendingOverlay() {
  const location = useLocation();
  const pathname = location.pathname;

  /** `null` = no successful profile fetch yet; use login `isKYCSubmitted` until then. After fetch, trust API only. */
  const [profileIsKYCSubmitted, setProfileIsKYCSubmitted] = useState(null);

  const pending = useMemo(() => {
    if (!getToken()) return false;
    if (isPublicPath(pathname)) return false;
    const d = getLoginDetails() || {};
    if (d.hierarchy !== 2) return false;
    const fromLogin = !!d.isKYCSubmitted;
    if (profileIsKYCSubmitted === null) {
      return !fromLogin;
    }
    return !profileIsKYCSubmitted;
  }, [pathname, profileIsKYCSubmitted]);

  useEffect(() => {
    if (!getToken() || isPublicPath(pathname)) return;
    const d = getLoginDetails() || {};
    if (d.hierarchy !== 2) return;
    if (profileIsKYCSubmitted === true) return;

    let cancelled = false;

    const fetchProfile = async () => {
      try {
        const raw = await getData(GET_USER_DETAILS_URL);
        if (cancelled) return;
        const root = normalizeUserDetailsPayload(raw);
        const submitted = readIsKYCSubmitted(root);
        setProfileIsKYCSubmitted(submitted);
        storeLoginDetails({
          ...getLoginDetails(),
          isKYCSubmitted: submitted,
        });
      } catch (e) {
        /* keep polling */
      }
    };

    fetchProfile();
    const id = setInterval(fetchProfile, PROFILE_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [pathname, profileIsKYCSubmitted]);

  const [modalOpen, setModalOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return sessionStorage.getItem(SESSION_CLOSED_KEY) !== "1";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem("kyc-pending-open") === "1") {
      sessionStorage.removeItem("kyc-pending-open");
      sessionStorage.removeItem(SESSION_CLOSED_KEY);
      setModalOpen(true);
    }
  }, [pending]);

  const handleClose = useCallback(() => {
    sessionStorage.setItem(SESSION_CLOSED_KEY, "1");
    setModalOpen(false);
  }, []);

  const handleResume = useCallback(() => {
    sessionStorage.removeItem(SESSION_CLOSED_KEY);
    setModalOpen(true);
  }, []);

  const handleKycSuccess = useCallback(() => {
    sessionStorage.removeItem(SESSION_CLOSED_KEY);
    window.location.reload();
  }, []);

  if (!pending) return null;

  return (
    <div className="kyc-pending-overlay" aria-live="polite">
      <div className="kyc-pending-overlay__scrim" aria-hidden="true" />

      {!modalOpen ? (
        <div className="kyc-pending-overlay__banner">
          <span>KYC required — complete the questionnaire to use the app.</span>
          <div className="kyc-pending-overlay__banner-actions">
            <button type="button" className="kyc-pending-overlay__resume" onClick={handleResume}>
              Resume KYC
            </button>
            <Link to="/logout" className="kyc-pending-overlay__logout">
              Log out
            </Link>
          </div>
        </div>
      ) : null}

      {modalOpen ? (
        <div className="kyc-pending-overlay__modal" role="dialog" aria-modal="true" aria-label="Know Your Client">
          <div className="kyc-pending-overlay__modal-header">
            <button
              type="button"
              className="kyc-pending-overlay__close"
              onClick={handleClose}
              aria-label="Close KYC form and fill it later"
            >
              CLOSE OR FILL IT LATER
            </button>
          </div>
          <div className="kyc-pending-overlay__modal-body">
            <KycForm variant="embed" showBranding onSuccess={handleKycSuccess} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
